import { useEffect, useState } from 'react'

export interface HydrantData {
  id: number
  name: string
  external_number?: string | null
  address?: string | null
  latitude: string
  longitude: string
  is_flushing_point: boolean
  notes?: string | null
  inspection_status?: string | null // 'passed' | 'failed' | 'unchecked'
  // Dane z ostatniego przeglądu (tylko do odczytu; aktualizowane importem)
  diameter?: string | null
  coefficient_k?: number | null
  pressure_dynamic?: number | null
  pressure_static?: number | null
  flow_rate?: number | null
  measurement_date?: string | null
  inspection_note?: string | null
}

interface HydrantEditModalProps {
  isOpen: boolean
  hydrant: HydrantData | null
  onClose: () => void
  onSave: (id: number, data: { name: string; address: string; is_flushing_point: boolean; notes: string; inspection_status: string }) => void
  onDelete: (id: number) => void
}

export default function HydrantEditModal({ isOpen, hydrant, onClose, onSave, onDelete }: HydrantEditModalProps) {
  const [name, setName] = useState('')
  const [address, setAddress] = useState('')
  const [isFlushingPoint, setIsFlushingPoint] = useState(false)
  const [notes, setNotes] = useState('')
  const [inspectionStatus, setInspectionStatus] = useState('unchecked')
  const [confirmDelete, setConfirmDelete] = useState(false)

  useEffect(() => {
    if (hydrant) {
      setName(hydrant.name || '')
      setAddress(hydrant.address || '')
      setIsFlushingPoint(!!hydrant.is_flushing_point)
      setNotes(hydrant.notes || '')
      setInspectionStatus(hydrant.inspection_status || 'unchecked')
      setConfirmDelete(false)
    }
  }, [hydrant])

  if (!isOpen || !hydrant) return null

  const handleSave = () => {
    if (!name.trim()) return
    onSave(hydrant.id, {
      name: name.trim(),
      address: address.trim(),
      is_flushing_point: isFlushingPoint,
      notes: notes.trim(),
      inspection_status: inspectionStatus,
    })
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-stretch justify-center z-[9999] sm:items-center sm:p-4" onClick={onClose}>
      <div className="bg-white sm:rounded-xl shadow-2xl w-full sm:max-w-md flex flex-col max-h-full sm:max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 sm:px-6 py-4 border-b border-gray-200 shrink-0">
          <h2 className="text-lg font-semibold text-gray-900">🚒 Edycja hydrantu</h2>
          <button onClick={onClose} className="p-1 -mr-1 text-gray-400 hover:text-gray-600 text-xl font-bold">✕</button>
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 overflow-y-auto px-5 sm:px-6 py-4 space-y-4">
          <div className="bg-gray-50 rounded-lg px-4 py-2 text-sm text-gray-600">
            📍 Lokalizacja: {parseFloat(hydrant.latitude).toFixed(6)}, {parseFloat(hydrant.longitude).toFixed(6)}
          </div>

          {/* Dane z ostatniego przeglądu (tylko do odczytu) */}
          {(hydrant.measurement_date || hydrant.coefficient_k != null || hydrant.pressure_dynamic != null ||
            hydrant.flow_rate != null || hydrant.pressure_static != null || hydrant.diameter || hydrant.inspection_note) && (
            <div className="rounded-lg border border-gray-200 p-3 space-y-1.5">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Ostatni przegląd</p>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                {hydrant.measurement_date && <Row label="Data kontroli" value={hydrant.measurement_date} />}
                {hydrant.diameter && <Row label="Średnica" value={hydrant.diameter} />}
                {hydrant.coefficient_k != null && <Row label="Współczynnik K" value={String(hydrant.coefficient_k)} />}
                {hydrant.pressure_dynamic != null && <Row label="Ciśn. dynamiczne" value={`${hydrant.pressure_dynamic} bar`} />}
                {hydrant.pressure_static != null && <Row label="Ciśn. statyczne" value={`${hydrant.pressure_static} bar`} />}
                {hydrant.flow_rate != null && <Row label="Wydajność" value={`${hydrant.flow_rate} l/s`} />}
                {hydrant.external_number && <Row label="Nr źródłowy" value={hydrant.external_number} />}
              </div>
              {hydrant.inspection_note && (
                <p className="text-sm text-amber-800 bg-amber-50 rounded px-2 py-1">📝 {hydrant.inspection_note}</p>
              )}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nazwa / ID hydrantu <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Lokalizacja administracyjna (adres)
            </label>
            <input
              type="text"
              value={address}
              onChange={e => setAddress(e.target.value)}
              placeholder="np. ul. Główna 12, Miejscowość"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <label className="flex items-center gap-3 bg-orange-50 border border-orange-200 rounded-lg px-4 py-3 cursor-pointer">
            <input
              type="checkbox"
              checked={isFlushingPoint}
              onChange={e => setIsFlushingPoint(e.target.checked)}
              className="w-5 h-5 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
            />
            <span className="text-sm font-medium text-gray-800">💦 Punkt płukania sieci</span>
          </label>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Wynik kontroli ciśnienia i wydajności
            </label>
            <StatusPicker value={inspectionStatus} onChange={setInspectionStatus} />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Uwagi (opcjonalnie)
            </label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={3}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3 px-5 sm:px-6 py-4 border-t border-gray-200 shrink-0">
          {confirmDelete ? (
            <div className="flex items-center gap-2">
              <span className="text-sm text-red-600">Na pewno?</span>
              <button
                onClick={() => onDelete(hydrant.id)}
                className="min-h-[44px] px-3 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg"
              >
                Tak, usuń
              </button>
              <button
                onClick={() => setConfirmDelete(false)}
                className="min-h-[44px] px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg"
              >
                Nie
              </button>
            </div>
          ) : (
            <button
              onClick={() => setConfirmDelete(true)}
              className="min-h-[44px] px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg sm:mr-auto"
            >
              🗑 Usuń
            </button>
          )}

          <div className="flex flex-col-reverse sm:flex-row gap-3">
            <button
              onClick={onClose}
              className="min-h-[44px] px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              Anuluj
            </button>
            <button
              onClick={handleSave}
              disabled={!name.trim()}
              className="min-h-[44px] px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Zapisz zmiany
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-2">
      <span className="text-gray-500">{label}</span>
      <span className="text-gray-900 text-right">{value}</span>
    </div>
  )
}

const STATUS_OPTIONS: { value: string; label: string; dot: string; active: string }[] = [
  { value: 'passed', label: 'Pozytywny', dot: 'bg-green-600', active: 'border-green-600 bg-green-50 text-green-800' },
  { value: 'failed', label: 'Negatywny', dot: 'bg-red-600', active: 'border-red-600 bg-red-50 text-red-800' },
  { value: 'unchecked', label: 'Niesprawdzony', dot: 'bg-gray-400', active: 'border-gray-500 bg-gray-100 text-gray-800' },
]

export function StatusPicker({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div className="grid grid-cols-3 gap-2">
      {STATUS_OPTIONS.map(opt => {
        const selected = value === opt.value
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            className={`flex items-center justify-center gap-1.5 min-h-[44px] px-2 py-2 rounded-lg border text-sm font-medium transition-colors ${
              selected ? opt.active : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            <span className={`w-3 h-3 rounded-full ${opt.dot}`} />
            {opt.label}
          </button>
        )
      })}
    </div>
  )
}
